<template>
  <ComingSoon v-if="blocked" />
  <template v-else>
    <Smartbanner v-if="isMobileAppNotice && BrowserService.isMobile() && showSmartbanner && !BrowserService.isMobileWebview()" />
    <NoticeOnTop v-if="BrowserService.isDesktop() && showNoticeOnTop" />
    <div class="min-h-[80vh]">
      <template v-if="BrowserService.isDesktop() && layoutType === 'base'">
        <Navbar />
        <NavbarSecondary />
      </template>
      <NavbarCheckout v-if="layoutType === 'checkout'" />
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
      <template v-if="BrowserService.isDesktop()">
        <ScrollUpButton />
      </template>
    </div>
    <CookieBar v-if="!isConsentsSet && BrowserService.isDesktop()" />
    <ModalMobileMenu />
    <ModalMobileSearch />
    <NoticeToasts />
    <MrBird
      v-if="birdNotices.length && isMrBirdOpened"
      :message="birdNotices[0].text"
      @close-mr-bird="closeMrBird"
    />
    <BaseModal modal-id="modal-alert">
      <ModalAlert />
    </BaseModal>
    <BaseModal
      :title="t('choose_type_of_mix_title')"
      modal-id="push-to-mix-modal"
    >
      <ModalPushToMix />
    </BaseModal>
    <BaseModal
      v-if="BrowserService.isDesktop()"
      :title="t('log_in')"
      modal-id="modal-sign-in"
    >
      <ModalSignIn />
    </BaseModal>
    <BaseModal
      v-if="BrowserService.isDesktop()"
      :title="t('modal_title')"
      modal-id="modal-cookies"
    >
      <ModalCookies />
    </BaseModal>
    <BaseModal
      :title="t('more_you_buy')"
      modal-id="modal-quantity-discount-table"
    >
      <ModalQuantityDiscountTable />
    </BaseModal>
    <BaseModal
      :title="t('remove_item_title')"
      modal-id="modal-remove-item"
    >
      <ModalRemoveItem />
    </BaseModal>
    <FooterEtrusted v-if="locale === 'de'" />
  </template>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onBeforeUnmount, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import useHead from '@/mixins/headManager'
import { isString } from '@/mixins/typeCheck'
import { appCheck } from '@/mixins/useAppRefresher'
import BrowserService from '@/services/browserService'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'
import { useCategoriesStore } from '@/stores/CategoriesStore'
import RetailApiV2 from '@/api/retailApi/RetailApiV2'

import Navbar from '@/components/menus/navbar/TheNavbar.vue'
import NavbarSecondary from '@/components/menus/navbar_secondary/TheNavbarSecondary.vue'
import NavbarCheckout from '@/components/menus/navbar/NavbarCheckout.vue'
import { dispatchEvent } from "@/mixins/webViewUtils.ts";
import FooterEtrusted from "@/components/footer/FooterEtrusted.vue";
import { useCountry } from "@/mixins/useCountry";

const country = useCountry()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const urlParams = new URLSearchParams(window.location.search)
const categoriesStore = useCategoriesStore()
const appStore = useAppStore()
const { isConsentsSet, isMobileAppNotice } = storeToRefs(appStore)
const cartStore = useCartStore()

const ModalCookies = defineAsyncComponent(() => import('@/components/modals/ModalCookies.vue'))
const ScrollUpButton = defineAsyncComponent(() => import('@/components/buttons/ButtonScrollUp.vue'))
const BaseModal = defineAsyncComponent(() => import('@/components/general/BaseModal.vue'))
const CookieBar = defineAsyncComponent(() => import('@/components/general/CookieBar.vue'))
const ModalAlert = defineAsyncComponent(() => import('@/components/modals/ModalAlert.vue'))
const ModalMobileMenu = defineAsyncComponent(() => import('@/components/modals/ModalMobileMenu.vue'))
const ModalMobileSearch = defineAsyncComponent(() => import('@/components/modals/ModalMobileSearch.vue'))
const ModalPushToMix = defineAsyncComponent(() => import('@/components/modals/ModalPushToMix.vue'))
const ModalQuantityDiscountTable = defineAsyncComponent(
  () => import('@/components/modals/ModalQuantityDiscountTable.vue'),
)
const ModalRemoveItem = defineAsyncComponent(() => import('@/components/modals/ModalRemoveItem.vue'))
const ModalSignIn = defineAsyncComponent(() => import('@/components/modals/ModalSignIn.vue'))
const NoticeOnTop = defineAsyncComponent(() => import('@/components/notices/NoticeOnTop.vue'))
const NoticeToasts = defineAsyncComponent(() => import('@/components/notices/NoticeToasts.vue'))
const Smartbanner = defineAsyncComponent(() => import('@/components/smartbanner/Smartbanner.vue'))
const MrBird = defineAsyncComponent(() => import('@/components/general/BaseMrBird.vue'))
const ComingSoon = defineAsyncComponent(() => import('@/views/coming_soon/index.vue'))

const showSmartbanner = computed(() => {
  const dateCreated = Date.parse(localStorage.getItem('smartBanner'))
  if (dateCreated) {
    const today = new Date()
    const expired = (today - dateCreated) / (1000 * 3600 * 24)
    return expired > 30
  }
  return true
})

// show marketing notice only if you are not on certain page
const showNoticeOnTop = computed(() => {
  return route.name !== 'easterGame'
})

const DISABLED_COUNTRIES = ['it']
const QA_ACCESS_KEY = 'qa_access'

const hasQaAccess = () => localStorage.getItem(QA_ACCESS_KEY) === '1'
const grantQaAccess = () => localStorage.setItem(QA_ACCESS_KEY, '1')

const blocked = computed(() =>
  DISABLED_COUNTRIES.includes(country.toLowerCase()) &&
  !('qa_access' in route.query) &&
  !hasQaAccess()
)

watch(
  () => route.query,
  (query) => {
    if (!('qa_access' in query)) return
    grantQaAccess()
    const newQuery = { ...query }
    delete newQuery.qa_access
    router.replace({ query: newQuery })
  },
  { immediate: true }
)

// Auto login
if (isString(urlParams.get('token'))) {
  localStorage.token = urlParams.get('token')
}

categoriesStore.loadCategories()

useHead({
  title: t('title'),
  meta: [
    {
      property: `og:title`,
      content: t('title'),
    },
    {
      property: `og:description`,
      content: t('description'),
    },
    {
      property: `og:url`,
      content: import.meta.env.VITE_VUE_APP_ORIGINAL_URL,
    },
    {
      name: `description`,
      content: t('description'),
    },
    {
      property: `og:image`,
      content: `${import.meta.env.VITE_VUE_APP_ORIGINAL_URL}structured_data/default_og_image.jpg`,
    },
  ],
})

appStore.setAppVersion()
appStore.fetchConfig()
appStore.fetchCheckoutNotice()
appCheck()

const layoutType = computed(() => route.meta.layout ?? '')

// Used to update cart badge in mobile app
const unwatch = watch(
  () => cartStore.getTotalItemsInCart,
  (newValue) => {
    dispatchEvent({
      event: 'updateCartBadge',
      quantity: newValue,
    });
  },
)

const birdNotices = ref([])
const isMrBirdOpened = ref(true);

const closeMrBird = () => { isMrBirdOpened.value = false; }

const fetchMrBirdNotice = async () => {
  const { data } = await RetailApiV2.get('notices?severity=alert&limit=1');
  if (!data.notices || (localStorage.getItem('birdNoticeValidUntil') && new Date() < new Date(localStorage.getItem('birdNoticeValidUntil')))) {
    isMrBirdOpened.value = false;
  }
  for (const item of data.notices) {
    item.text = item.text.replaceAll(/<p[^>]*>/gu, '')?.replace(/<\/?p[^>]*>/gu, '')
    birdNotices.value.push(item)
  }
  if (birdNotices.value.length > 0 && new Date() > new Date(localStorage.getItem('birdNoticeValidUntil'))) {
    const currentDate = new Date();
    localStorage.setItem('birdNoticeValidUntil', new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))
  }
}
fetchMrBirdNotice();

onBeforeUnmount(() => {
  unwatch()
})
</script>

<i18n>
  {
    "cs": {
    "choose_type_of_mix_title": "Zvolte, jaký typ mixu chcete míchat:",
    "description": "Na Mixit.cz si můžete namíchat svoje müsli, kaše nebo mix ovoce, oříšků a čokolády. Vše vám v krásném tubusu pošleme až domů nebo si vyzvednete na jednom z odběrných míst.",
    "log_in": "Přihlášení",
    "modal_title": "Cookies",
    "more_you_buy": "Čím víc nakoupíš, tím víc ušetříš.",
    "remove_item_title": "Opravdu si přejete odstranit tento produkt z košíku?",
    "title": "Namíchej si svoje müsli"
},
    "sk": {
    "choose_type_of_mix_title": "Zvoľte, aký typ mixu chcete miešať:",
    "description": "Na Mixit.sk si môžete namiešať svoje müsli, kašu alebo mix ovocia, orieškov a čokolády. Všetko Vám v krásnom tubuse pošleme až domov alebo si dobroty vyzdvihnete na jednom z odberných miest.",
    "log_in": "Prihlásenie",
    "modal_title": "Cookies",
    "more_you_buy": "Čím viac nakúpiš, tým viac ušetríš.",
    "remove_item_title": "Naozaj si želáte odstrániť tento produkt z košíka?",
    "title": "Namiešajte si svoje müsli"
},
    "pl": {
    "choose_type_of_mix_title": "Wybierz jaki rodzaj mieszanki chcesz przygotować:",
    "description": "Na Mixit.pl możesz stworzyć własny mix müsli, kaszy lub owoców, orzechów czy czekolady. Wszystko to w pięknej tubie doręczymy pod wskazany adres lub do punktu odbioru.",
    "log_in": "Logowanie",
    "modal_title": "Pliki cookies",
    "more_you_buy": "Im więcej kupisz, tym więcej zaoszczędzisz.",
    "remove_item_title": "Naprawdę chcesz usunąć ten produkt z koszyka?",
    "title": "Stwórz swoje müsli"
},
    "hu": {
    "choose_type_of_mix_title": "Válaszd ki milyen fajta mixet szeretnél keverni!",
    "description": "A Mixit.hu oldalon saját müzlit, zabkását vagy gyümölcs-, dió- és csokoládékeverékét állíthatsz össze. Gyönyörű dobozba csomagolva küldjük házhoz, vagy átveheted az ingyenes átvevő pontokon.",
    "log_in": "Bejelentkezés",
    "modal_title": "Sütik",
    "more_you_buy": "Minél többet vásárolsz, annál többet takarítasz meg.",
    "remove_item_title": "Biztos el szeretnéd távolítani a terméket a kosárból?",
    "title": "Keverj saját müzlit"
},
    "de": {
    "choose_type_of_mix_title": "Wähle, welche Art von Mix du zusammenstellen möchtest.",
    "description": "Bei Mixit.de kannst du dir Müsli, Porridge oder eine Mischung aus Früchten, Nüssen und Schokolade individuell mixen. Wir schicken dir alles in einer schönen Dose nach Hause.",
    "log_in": "Anmeldung",
    "modal_title": "Cookies",
    "more_you_buy": "Je mehr du kaufst, desto mehr sparst du.",
    "remove_item_title": "Möchtest du dieses Produkt wirklich aus dem Warenkorb entfernen?",
    "title": "Mix dir dein eigenes Müsli"
},
    "at": {
    "choose_type_of_mix_title": "Wähle, welche Art von Mix du zusammenstellen möchtest.",
    "description": "Bei Mixit.de kannst du dir Müsli, Porridge oder eine Mischung aus Früchten, Nüssen und Schokolade individuell mixen. Wir schicken dir alles in einer schönen Dose nach Hause.",
    "log_in": "Anmeldung",
    "modal_title": "Cookies",
    "more_you_buy": "Je mehr du kaufst, desto mehr sparst du.",
    "remove_item_title": "Möchtest du dieses Produkt wirklich aus dem Warenkorb entfernen?",
    "title": "Mix dir dein eigenes Müsli"
},
    "ro": {
    "choose_type_of_mix_title": "Alegeti tipul mixului pentru amestecul dumneavoastra:",
    "description": "La Mixit.ro îți poți amesteca propriul musli, terci, sau mix de fructe, nuci și ciocolată. Totul îți este trimis într-un tub frumos, direct la domiciliu sau îl puteți ridica de la unul dintre punctele de colectare.",
    "log_in": "Intră în cont",
    "modal_title": "Cookies",
    "more_you_buy": "Cu cât cumperi mai mult, cu atât economisești mai mult.",
    "remove_item_title": "Chiar dorești să îndepărtezi produsul din coș?",
    "title": "Amestecă propriul mix"
},
    "fr": {
    "choose_type_of_mix_title": "Choisissez le type de mix que vous souhaitez créer :",
    "description": "Sur Mixit.fr, créez votre propre muesli, porridge ou mélange de fruits, de noix et de chocolat. Nous vous livrons le tout dans un joli tube, directement chez vous ou à retirer dans l’un de nos points de retrait.",
    "log_in": "Connexion",
    "modal_title": "Cookies",
    "more_you_buy": "Plus vous achetez, plus vous économisez.",
    "remove_item_title": "Voulez-vous vraiment supprimer ce produit de votre panier ?",
    "title": "Crée ton propre muesli"
},
    "it": {
    "choose_type_of_mix_title": "Scegli il tipo di mix che vuoi creare:",
    "description": "Su Mixit.it puoi mescolare il tuo muesli, porridge o mix di frutta, noci e cioccolato. Te lo spediamo dritto a casa tua in un bellissimo tubo colorato oppure puoi ritirarlo in uno dei nostri punti di ritiro.",
    "log_in": "Accedi",
    "modal_title": "Cookie",
    "more_you_buy": "Più compri, più risparmi.",
    "remove_item_title": "Sei sicuro di voler rimuovere questo prodotto dal carrello?",
    "title": "Mescola il tuo muesli"
}
  }
</i18n>
